'use client'

import { useIsBreakpoint } from '@/hooks/use-breakpoint'

/** Split into `count` sequential, balanced slices (down-column reading order). */
const toColumns = (items: React.ReactNode[], count: number): React.ReactNode[][] => {
    const perColumn = Math.ceil(items.length / count)
    return Array.from({ length: count }, (_, i) =>
        items.slice(i * perColumn, (i + 1) * perColumn),
    ).filter((column) => column.length > 0)
}

/**
 * Arranges pre-rendered destination items into independent column stacks.
 *
 * Takes **rendered nodes**, not route data — the items are built on the server
 * so their links resolve their locale prefix before serialization, and this
 * component never sees a route.
 * It is deliberately incurious about what it is arranging.
 *
 * The column *count* has to be resolved client-side. Independent stacks require
 * chunking the list to the active count, and no CSS primitive available today
 * does that without moving items between columns when one opens: `column-count`
 * re-balances (measured — opening one of 30 items relocated 6 of them), and
 * `grid-auto-flow: column` shares row heights across columns. `display:
 * grid-lanes` is the right primitive and is still flag-gated everywhere. Until
 * JS resolves the count (SSR / no-JS), the list renders as a single column with
 * every link present.
 */
export const PlanColumns = ({ items }: { items: React.ReactNode[] }) => {
    // 1 column on mobile, 2 on tablet (sm–lg), 3 on desktop (lg+). Defaults to a
    // single column for SSR / no-JS, then resolves on the client.
    const belowSm = useIsBreakpoint('sm', { initializeWithValue: false, defaultValue: true })
    const belowLg = useIsBreakpoint('lg', { initializeWithValue: false, defaultValue: true })
    const columnCount = belowSm ? 1 : belowLg ? 2 : 3

    return (
        <div
            data-testid="plan-columns"
            className="mx-auto mt-8 flex max-w-[1100px] items-start gap-x-10"
        >
            {toColumns(items, columnCount).map((column, i) => (
                <ul key={i} className="flex-1">
                    {column}
                </ul>
            ))}
        </div>
    )
}
